import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import GoogleAnalytics from "@/components/GoogleAnalytics";

import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import Bundles from "@/pages/Bundles";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import CheckoutReturn from "@/pages/CheckoutReturn";
import Library from "@/pages/Library";
import Orders from "@/pages/Orders";
import Club from "@/pages/Club";
import Login from "@/pages/Login";
import Blog from "@/pages/Blog";
import Blogs from "@/pages/Blogs";
import Page from "@/pages/Page";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminCRM from "@/pages/admin/AdminCRM";
import AdminBlogs from "@/pages/admin/AdminBlogs";
import AdminBundles from "@/pages/admin/AdminBundles";
import AdminImages from "@/pages/admin/AdminImages";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminPages from "@/pages/admin/AdminPages";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user || !user.roles?.includes("admin")) return <Navigate to="/admin/login" replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/login" element={<Layout><Login mode="login" /></Layout>} />
      <Route path="/signup" element={<Layout><Login mode="signup" /></Layout>} />

      {/* Public store */}
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/browse" element={<Layout><Browse /></Layout>} />
      <Route path="/bundles" element={<Layout><Bundles /></Layout>} />
      <Route path="/product/:id" element={<Layout><ProductDetail /></Layout>} />
      <Route path="/cart" element={<Layout><Cart /></Layout>} />
      <Route path="/checkout/return" element={<Layout><CheckoutReturn /></Layout>} />
      <Route path="/club" element={<Layout><Club /></Layout>} />
      <Route path="/library" element={<RequireAuth><Layout><Library /></Layout></RequireAuth>} />
      <Route path="/orders" element={<RequireAuth><Layout><Orders /></Layout></RequireAuth>} />

      {/* Blog */}
      <Route path="/blog" element={<Layout><Blogs /></Layout>} />
      <Route path="/blog/:slug" element={<Layout><Blog /></Layout>} />

      {/* CMS pages */}
      <Route path="/about" element={<Layout><Page /></Layout>} />
      <Route path="/contact" element={<Layout><Page /></Layout>} />
      <Route path="/terms" element={<Layout><Page /></Layout>} />
      <Route path="/privacy" element={<Layout><Page /></Layout>} />
      <Route path="/refund-policy" element={<Layout><Page /></Layout>} />
      <Route path="/disclaimer" element={<Layout><Page /></Layout>} />
      <Route path="/p/:slug" element={<Layout><Page /></Layout>} />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
      <Route path="/admin/products" element={<RequireAdmin><AdminProducts /></RequireAdmin>} />
      <Route path="/admin/bundles" element={<RequireAdmin><AdminBundles /></RequireAdmin>} />
      <Route path="/admin/categories" element={<RequireAdmin><AdminCategories /></RequireAdmin>} />
      <Route path="/admin/orders" element={<RequireAdmin><AdminOrders /></RequireAdmin>} />
      <Route path="/admin/customers" element={<RequireAdmin><AdminCustomers /></RequireAdmin>} />
      <Route path="/admin/blogs" element={<RequireAdmin><AdminBlogs /></RequireAdmin>} />
      <Route path="/admin/images" element={<RequireAdmin><AdminImages /></RequireAdmin>} />
      <Route path="/admin/pages" element={<RequireAdmin><AdminPages /></RequireAdmin>} />
      <Route path="/admin/newsletter" element={<RequireAdmin><AdminNewsletter /></RequireAdmin>} />
      <Route path="/admin/reviews" element={<RequireAdmin><AdminReviews /></RequireAdmin>} />
      <Route path="/admin/settings" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
      <Route path="/admin/crm" element={<RequireAdmin><AdminCRM /></RequireAdmin>} />

      <Route path="*" element={<Layout><div className="container-page py-24 text-center"><div className="font-display text-3xl">Page not found.</div></div></Layout>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <GoogleAnalytics />
          <AppRouter />
          <Toaster position="top-right" />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
